// The sky shared by the outdoor places (the farm's backdrop and the street): the colour
// gradient from the top of the screen down to the horizon, one sun and one moon (tonight's
// phase), all screen-fixed. Timing and colours come from skyClock.mjs, so two places never
// disagree about where the sun is. The caller builds the ctx each tick (FarmBackdrop.js,
// street/StreetSky.js): { t, now, night, horizonY, panX, viewW, viewH, dtMs }.
import { GAME_WIDTH } from '../config.js';
import { gameState } from '../state/gameState.js';
import { skyColours, sunPos, moonPos } from './skyClock.mjs';
import { skyToScreen } from './backdropLayout.mjs';
import { DEPTH } from './backdropDepth.mjs';
import { subpixel } from './subpixel.js';
import { moonPhaseFor, moonNight } from './moonPhase.mjs';
import { ensureMoonPhaseTextures, moonPhaseKey } from './moonPhaseTextures.js';
import { serverNow } from '../state/serverClock.mjs';

const SUN_FACTOR = 0.05;
const BODIES = {
  sun: { key: 'farm_bd_sun', color: 0xfff2a0, r: 26 },
  moon: { key: 'farm_bd_moon', color: 0xf0f0ff, r: 18 },
};

export function clockInput(now = serverNow()) {
  const time = gameState.raw?.time;
  return { now, anchorIso: time?.cycle_started_at_iso, devPhase: gameState._devPhase, serverPhase: time?.phase };
}

function makeBody(scene, { key, color, r }) {
  const obj = scene.textures.exists(key)
    ? scene.add.image(0, 0, key).setScale(0.5)
    : scene.add.circle(0, 0, r, color, 1);
  return subpixel(scene, obj, DEPTH.sunMoon).setVisible(false);
}

export function createSkyLayer(scene) {
  const sky = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH.sky);
  const sun = makeBody(scene, BODIES.sun);
  const moon = makeBody(scene, BODIES.moon);
  // Eight phases, one per night (moonPhase.mjs); the moon image swaps to tonight's texture.
  const hasPhases = ensureMoonPhaseTextures(scene);
  let moonPhaseOverride = null;   // debugMoonPhase()
  let skySig = '';

  const placeBody = (obj, p, ctx) => {
    obj.setVisible(p.visible);
    if (!p.visible) return;
    // The sun and moon track the horizon, like everything else in the backdrop: that is what
    // makes them rise from and set behind the hills.
    const s = skyToScreen(p.u, p.v, { horizonY: ctx.horizonY, viewW: ctx.viewW, panX: ctx.panX, factor: SUN_FACTOR, spread: 1 });
    obj.setPosition(s.x, s.y);
  };

  return {
    update(ctx) {
      const { top, bottom } = skyColours(ctx.t);
      const skyH = Math.max(0, Math.ceil(ctx.horizonY) + 2);
      const sig = `${top}|${bottom}|${skyH}`;
      if (sig !== skySig) {
        skySig = sig;
        sky.clear();
        if (skyH > 0) {
          sky.fillGradientStyle(top, top, bottom, bottom, 1);
          sky.fillRect(0, 0, GAME_WIDTH, skyH);
        }
      }
      placeBody(sun, sunPos(ctx.t), ctx);
      placeBody(moon, moonPos(ctx.t), ctx);
      if (hasPhases) {
        const phase = moonPhaseOverride ?? moonPhaseFor(moonNight(ctx.t, ctx.night));
        const img = moon.list[0];
        if (img.texture.key !== moonPhaseKey(phase)) img.setTexture(moonPhaseKey(phase));
      }
    },
    destroy() { for (const o of [sky, sun, moon]) o.destroy(); },
    debugMoonPhase(phase) { moonPhaseOverride = phase; },
  };
}
